import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

/**
 * Comment form for visitors with no Trados account.
 *
 * The captcha is served by the backend (services/captcha.py): a signed,
 * single-use arithmetic challenge. It is fetched on mount and re-fetched after
 * every submit, because a token can only be spent once — reusing it would fail
 * the second attempt with "already used".
 *
 * `website` is a honeypot: hidden from people, irresistible to bots, and the
 * backend silently discards anything that fills it.
 */
const PublicCommentForm = ({ slug, onSubmitted }) => {
  const [form, setForm] = useState({ name: '', email: '', text: '', answer: '', website: '' });
  const [challenge, setChallenge] = useState(null);
  const [loadingCaptcha, setLoadingCaptcha] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadCaptcha = useCallback(async () => {
    setLoadingCaptcha(true);
    try {
      const res = await api.get('/public/directory/captcha');
      setChallenge(res.data);
    } catch {
      setChallenge(null);
    } finally {
      setLoadingCaptcha(false);
    }
  }, []);

  useEffect(() => { loadCaptcha(); }, [loadCaptcha]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const ready =
    form.name.trim().length >= 2 &&
    /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(form.email.trim()) &&
    form.text.trim().length >= 3 &&
    form.answer.trim() !== '' &&
    !!challenge;

  const submit = async () => {
    if (!ready) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/public/directory/${slug}/comments`, {
        name: form.name.trim(),
        email: form.email.trim(),
        text: form.text.trim(),
        captcha_token: challenge.token,
        captcha_answer: form.answer.trim(),
        website: form.website,
      });
      toast.success(res.data.message || 'Thank you — your comment is awaiting approval.');
      setForm({ name: '', email: '', text: '', answer: '', website: '' });
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit your comment.');
    } finally {
      // Token is spent either way — a fresh one avoids a confusing second failure.
      setForm((f) => ({ ...f, answer: '' }));
      loadCaptcha();
      setSubmitting(false);
    }
  };

  const input = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent';

  return (
    <div className="mt-4 space-y-3" data-testid="public-comment-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          value={form.name}
          onChange={set('name')}
          maxLength={80}
          placeholder="Your name *"
          data-testid="public-comment-name"
          className={input}
        />
        <input
          value={form.email}
          onChange={set('email')}
          type="email"
          maxLength={120}
          placeholder="Your email *"
          data-testid="public-comment-email"
          className={input}
        />
      </div>

      <textarea
        value={form.text}
        onChange={set('text')}
        rows={3}
        maxLength={1000}
        placeholder="Share your experience with this company… *"
        data-testid="public-comment-text"
        className={`${input} resize-none`}
      />

      {/* Honeypot — off-screen rather than display:none, which some bots skip */}
      <input
        value={form.website}
        onChange={set('website')}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute w-px h-px -left-[9999px] opacity-0"
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600" data-testid="public-comment-captcha-question">
            {loadingCaptcha ? 'Loading…' : (challenge?.question || 'Captcha unavailable')}
          </span>
          <button
            type="button"
            onClick={loadCaptcha}
            title="New question"
            aria-label="Get a new captcha question"
            className="text-gray-400 hover:text-gray-600"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingCaptcha ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <input
          value={form.answer}
          onChange={set('answer')}
          inputMode="numeric"
          maxLength={4}
          placeholder="Answer"
          data-testid="public-comment-captcha"
          className={`${input} w-24`}
        />
        <button
          type="button"
          onClick={submit}
          disabled={!ready || submitting}
          data-testid="public-comment-submit"
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: BRAND_BLUE }}
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Post comment
        </button>
      </div>

      <p className="text-[11px] text-gray-400">
        Your email stays private — it is only used by the Trados team to verify the comment.
      </p>
    </div>
  );
};

export default PublicCommentForm;
